/* eslint-disable no-nested-ternary */
import { useTranslation } from 'react-i18next'
import { Button, Grid, Typography, CircularProgress } from '@mui/material'
import useDState from '@betterfield/dstate-react-js'
import useStyles from './styles'
import { ReactComponent as SuccessIcon } from '../../../../../../assets/Icons_Success.svg'
import { ReactComponent as ErrorIcon } from '../../../../../../assets/Icons_Error.svg'

// Component for when we have a dStateId
function ResultStepWithDState({ dStateId, loadingMessage, error, onConfirm }) {
  const { t } = useTranslation()
  const classes = useStyles()

  // Handle onConfirm with a small delay to allow cleanup
  const handleConfirm = () => {
    // Small delay to allow any pending requests to complete/cancel gracefully
    setTimeout(onConfirm, 100)
  }

  const { isIdle, isInProgress, isDone, isError } = useDState(dStateId)

  const Errors = {
    ERROR_UPLOADING_FILE: 'ERROR_UPLOADING_FILE',
  }

  const message = loadingMessage
    ? loadingMessage
    : isError
    ? t('create-chassis-dialog.bulk-upload-wizard.results-dstate-error')
    : isDone
    ? t('create-chassis-dialog.bulk-upload-wizard.results-success')
    : isInProgress || isIdle
    ? t('create-chassis-dialog.bulk-upload-wizard.results-uploading-file-data')
    : t('create-chassis-dialog.bulk-upload-wizard.results-loading')

  return (
    <Grid
      container
      direction="column"
      justifyContent="space-between"
      alignItems="center"
      spacing={4}
    >
      {!isError && !isDone && !error && (
        <Grid item>
          <CircularProgress
            size="6rem"
            visibility={!isError && !isDone ? 'visible' : 'hidden'}
          />
        </Grid>
      )}
      {(!error && (
        <>
          {isDone && (
            <Grid item>
              <SuccessIcon visibility={isDone ? 'visible' : 'hidden'} />
            </Grid>
          )}
          {isError && (
            <Grid item>
              <ErrorIcon visibility={isError ? 'visible' : 'hidden'} />
            </Grid>
          )}
          <Grid item>
            <Typography className={classes.message}>{message}</Typography>
          </Grid>
        </>
      )) ||
        (error && (
          <>
            <Grid item>
              <ErrorIcon visibility={error ? 'visible' : 'hidden'} />
            </Grid>
            <Grid item>
              <Typography className={classes.message}>
                {error === Errors.ERROR_UPLOADING_FILE &&
                  t(
                    'create-vehicle-type-dialog.bulk-upload-wizard.results-dstate-error'
                  )}
              </Typography>
            </Grid>
          </>
        ))}
      <Grid item container justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          disabled={!isDone}
          onClick={handleConfirm}
        >
          Terminer
        </Button>
      </Grid>
    </Grid>
  )
}

// Component for when we don't have a dStateId
function ResultStepWithoutDState({
  loadingMessage,
  error,
  success,
  message: customMessage,
  onConfirm,
}) {
  const { t } = useTranslation()
  const classes = useStyles()

  const Errors = {
    ERROR_UPLOADING_FILE: 'ERROR_UPLOADING_FILE',
  }

  const message =
    customMessage || loadingMessage
      ? customMessage || loadingMessage
      : !error
      ? t('create-chassis-dialog.bulk-upload-wizard.results-tracking-error')
      : error

  const isSuccess = success && !error && !loadingMessage

  return (
    <Grid
      container
      direction="column"
      justifyContent="space-between"
      alignItems="center"
      spacing={4}
    >
      {!error && !success && loadingMessage && (
        <Grid item>
          <CircularProgress size="6rem" />
        </Grid>
      )}
      {isSuccess && (
        <>
          <Grid item>
            <SuccessIcon />
          </Grid>
          <Grid item>
            <Typography className={classes.message}>{message}</Typography>
          </Grid>
        </>
      )}
      {error && (
        <>
          <Grid item>
            <ErrorIcon />
          </Grid>
          <Grid item>
            <Typography className={classes.message}>
              {error === Errors.ERROR_UPLOADING_FILE
                ? t(
                    'create-vehicle-type-dialog.bulk-upload-wizard.results-dstate-error'
                  )
                : error}
            </Typography>
          </Grid>
        </>
      )}
      {!error && !loadingMessage && !success && (
        <>
          <Grid item>
            <ErrorIcon />
          </Grid>
          <Grid item>
            <Typography className={classes.message}>{message}</Typography>
          </Grid>
        </>
      )}
      <Grid item container justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          disabled={!isSuccess}
          onClick={onConfirm}
        >
          Terminer
        </Button>
      </Grid>
    </Grid>
  )
}

export default function ResultStep({
  loadingMessage,
  dStateId,
  error,
  success,
  message,
  onConfirm = () => {},
}) {
  // Render different component based on whether we have dStateId
  // This way hooks are always called in the same order within each component
  if (dStateId) {
    return (
      <ResultStepWithDState
        dStateId={dStateId}
        loadingMessage={loadingMessage}
        error={error}
        onConfirm={onConfirm}
      />
    )
  } else {
    return (
      <ResultStepWithoutDState
        loadingMessage={loadingMessage}
        error={error}
        success={success}
        message={message}
        onConfirm={onConfirm}
      />
    )
  }
}
